import camelcaseKeys from 'camelcase-keys'
import { createStore } from 'zustand'

import { executeDelete, executeGet, executePost } from '@/services/lukita'
import { removeItems } from '@/utils'
import type { Insights, InsightsDTO } from './types'

interface InsightsStoreActions {
  setInsights: (insights: Insights | InsightsDTO) => void
  startLoadingInsights: () => void
  stopLoadingInsights: () => void
  deleteDuplicates: (ids: number[]) => void
}
interface InsightsStoreMutations {
  createInsights: (slug: string) => Promise<void>
  deleteDuplicates: (slug: string, duplicates: number[]) => Promise<void>
}

interface InsightsStoreLoaders {
  fetchInsights: (slug: string) => Promise<void>
}

export interface InsightsStore {
  loadingInsights: boolean
  mutating: boolean
  insights: Insights | null
  actions: InsightsStoreActions
  mutations: InsightsStoreMutations
  loaders: InsightsStoreLoaders
}

export const createInsightsStore = () =>
  createStore<InsightsStore>((set, get) => ({
    loadingInsights: true,
    mutating: false,
    insights: null,
    actions: {
      startLoadingInsights: () => set({ loadingInsights: true }),
      stopLoadingInsights: () => set({ loadingInsights: false }),
      deleteDuplicates: (ids) =>
        set((state) => {
          if (!state.insights) return state
          return {
            insights: {
              ...state.insights,
              duplicates: removeItems(state.insights.duplicates ?? [], ids),
            },
          }
        }),
      setInsights: (insights) =>
        set({ insights: camelcaseKeys(insights, { deep: true }) as Insights }),
    },

    mutations: {
      deleteDuplicates: async (slug, ids) => {
        set({ mutating: true })
        const { setInsights, deleteDuplicates: optimisticDeleteDuplicates } =
          get().actions

        optimisticDeleteDuplicates(ids)

        const response = await executeDelete(
          `/projects/${slug}/insights/destroy_duplicates`,
          { ids },
        )
        const insights = await response.json()
        setInsights(insights)
        set({ mutating: false })
      },
      createInsights: async (slug) => {
        const { setInsights } = get().actions
        set({ mutating: true })

        try {
          const response = await executePost(`/projects/${slug}/insights`)
          const insights = await response.json()
          setInsights(insights)
        } finally {
          set({ mutating: false })
        }
      },
    },

    loaders: {
      fetchInsights: async (slug) => {
        const { setInsights, startLoadingInsights, stopLoadingInsights } =
          get().actions

        startLoadingInsights()
        try {
          const response = await executeGet(`/projects/${slug}/insights`)
          const insights = await response.json()
          setInsights(insights)
        } finally {
          stopLoadingInsights()
        }
      },
    },
  }))
